import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import { StorageLink } from '../global/storageUrls';

function NonControlledHistoryModal({ onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('non_controlled_medications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setHistory(data || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      alert('Error loading history.');
    } finally {
      setLoading(false);
    }
  };

  const parseMedItems = (jsonStr) => {
    if (!jsonStr) return [];
    try {
      return JSON.parse(jsonStr);
    } catch {
      return [];
    }
  };

  const downloadCSV = () => {
    if (history.length === 0) {
      alert('No data to download');
      return;
    }

    let csv = 'Date,Time,First Name,Last Name,Email,Action,Run Number,Truck Number,Medications,Signature,Notes\n';

    history.forEach(record => {
      const date = new Date(record.created_at);
      const formattedDate = date.toLocaleDateString('en-US');
      const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

      const medItems = parseMedItems(record.medication_items_json);
      const medsStr = medItems.map(item => {
        let s = `${item.medication}${item.concentration ? ' (' + item.concentration + ')' : ''} x${item.amount}`;
        if (item.expiration_date) s += ` [exp: ${item.expiration_date}]`;
        return s;
      }).join('; ');

      let row = [
        formattedDate,
        formattedTime,
        record.first_name || 'N/A',
        record.last_name || 'N/A',
        record.email || 'N/A',
        record.action || 'N/A',
        record.run_number || 'N/A',
        record.truck_number || 'N/A',
        medsStr || 'N/A',
        record.primary_signature_url || 'N/A',
        record.additional_notes || 'N/A'
      ];

      row = row.map(field => {
        const str = String(field).replace(/"/g, '""');
        return `"${str}"`;
      });

      csv += row.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'Non_Controlled_Medications_History.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'Patient Use Restock': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'Truck Restock': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'Inventory/Audit': return { bg: '#fff3e0', color: '#e65100' };
      case 'Removing Expired Medication': return { bg: '#fce4ec', color: '#c62828' };
      case 'Cabinet Inventory Restock': return { bg: '#f3e5f5', color: '#6a1b9a' };
      default: return { bg: '#f5f5f5', color: '#333' };
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.7)',
      overflowY: 'auto',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '40px',
        maxWidth: '95%',
        margin: '0 auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h2 style={{ color: '#333', fontSize: '24px', margin: 0 }}>
            Non-Controlled Medications History
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Download Button */}
        <div style={{ marginBottom: '20px' }}>
          <button
            className="btn btn-primary"
            onClick={downloadCSV}
            disabled={history.length === 0}
          >
            Download CSV
          </button>
          <span style={{ marginLeft: '15px', color: '#666', fontSize: '14px' }}>
            {history.length} record{history.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* History Table */}
        {loading ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Loading history...</p>
        ) : history.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666' }}>No history records found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#0E3293', color: 'white' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Time</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Action</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Details</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Medications</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Signature</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record, index) => {
                  const date = new Date(record.created_at);
                  const formattedDate = date.toLocaleDateString('en-US');
                  const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                  const medItems = parseMedItems(record.medication_items_json);
                  const actionColors = getActionColor(record.action);
                  const isExpanded = expandedRow === index;

                  let details = '';
                  if (record.run_number) details = `Run #: ${record.run_number}`;
                  if (record.truck_number) details = `Truck: ${record.truck_number}`;

                  return (
                    <tr key={index} style={{ background: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                      <td style={{ padding: '10px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>{formattedDate}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>{formattedTime}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                        {record.first_name} {record.last_name}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: actionColors.bg,
                          color: actionColors.color
                        }}>
                          {record.action}
                        </span>
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {details || 'N/A'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', minWidth: '200px' }}>
                        {medItems.length === 0 ? (
                          <span style={{ color: '#999' }}>N/A</span>
                        ) : (
                          <div>
                            {medItems.slice(0, isExpanded ? medItems.length : 2).map((item, i) => (
                              <div key={i} style={{
                                padding: '3px 0',
                                fontSize: '13px',
                                borderBottom: i < medItems.length - 1 ? '1px solid #eee' : 'none'
                              }}>
                                <strong>{item.medication}</strong>
                                {item.concentration && (
                                  <span style={{ color: '#666' }}> ({item.concentration})</span>
                                )}
                                <span style={{ color: '#0E3293', fontWeight: '600' }}> x{item.amount}</span>
                                {item.expiration_date && (
                                  <span style={{ color: '#c62828', fontSize: '12px' }}> [exp: {item.expiration_date}]</span>
                                )}
                              </div>
                            ))}
                            {medItems.length > 2 && !isExpanded && (
                              <button
                                onClick={() => setExpandedRow(index)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#0E3293',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '3px 0'
                                }}
                              >
                                +{medItems.length - 2} more...
                              </button>
                            )}
                            {isExpanded && medItems.length > 2 && (
                              <button
                                onClick={() => setExpandedRow(null)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#0E3293',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '3px 0'
                                }}
                              >
                                Show less
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                        {record.primary_signature_url ? (
                          <StorageLink value={record.primary_signature_url} style={{ color: '#0E3293' }}>
                            View
                          </StorageLink>
                        ) : 'N/A'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', maxWidth: '200px' }}>
                        {record.additional_notes || 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default NonControlledHistoryModal;
